"use client"

import { useState, useRef, useEffect } from "react"
import { Button } from "@/ui/shared/components/button"
import { User, Menu, X } from "lucide-react"
import Link from "next/link"
import type { Session } from "next-auth"
import { HeaderMobileNavigation } from "./header-mobile-navigation"
import { HeaderRegistrationButton } from "./header-registration-button"

interface HeaderMobileMenuProps {
    session: Session | null
    showAuthButtons?: boolean
}

export function HeaderMobileMenu({ session, showAuthButtons = true }: HeaderMobileMenuProps) {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
    const [headerHeight, setHeaderHeight] = useState(72) // default 4.5rem
    const mobileMenuRef = useRef<HTMLDivElement>(null)
    const buttonRef = useRef<HTMLButtonElement>(null)

    // Calculate header height
    useEffect(() => {
        const updateHeaderHeight = () => {
            const header = buttonRef.current?.closest('header')
            if (header) {
                setHeaderHeight(header.offsetHeight)
            }
        }

        updateHeaderHeight()
        window.addEventListener('resize', updateHeaderHeight)
        return () => window.removeEventListener('resize', updateHeaderHeight)
    }, [])

    // Close mobile menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setIsMobileMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    return (
        <>
            {/* Mobile Menu Button */}
            <button
                ref={buttonRef}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden flex items-center p-0 hover:opacity-70 transition-opacity duration-200"
            >
                {isMobileMenuOpen ? (
                    <X className="w-6 h-6 text-gray-600" />
                ) : (
                    <Menu className="w-6 h-6 text-gray-600" />
                )}
            </button>

            {/* Mobile Menu Dropdown - Full width */}
            {isMobileMenuOpen && (
                <div
                    ref={mobileMenuRef}
                    className="lg:hidden fixed inset-x-0 bg-white border-b border-gray-200 shadow-lg z-[9999]"
                    style={{ top: `${headerHeight - 1}px` }}
                >
                    <nav className="py-2 container mx-auto max-w-7xl">
                        <HeaderMobileNavigation onItemClick={() => setIsMobileMenuOpen(false)} />

                        {showAuthButtons && (
                            <>
                                <div className="border-t border-gray-200 my-2"></div>
                                {session ? (
                                    <div className="px-6 sm:px-8 py-2">
                                        <Button asChild variant="primary" className="w-full text-white py-2 onest-button">
                                            <Link href="/cp" onClick={() => setIsMobileMenuOpen(false)}>
                                                <User className="w-4 h-4 mr-2" />
                                                Личный кабинет
                                            </Link>
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="px-6 sm:px-8 py-2 space-y-2">
                                        <Button variant="outline" className="w-full onest-button text-gray-600 border-gray-300 hover:bg-gray-50 py-2">
                                            <Link href="/login" className="w-full" onClick={() => setIsMobileMenuOpen(false)}>Войти</Link>
                                        </Button>
                                        <HeaderRegistrationButton fullWidth className="w-full" />
                                    </div>
                                )}
                            </>
                        )}
                    </nav>
                </div>
            )}
        </>
    )
}

